import camelize from 'camelize';
import React from 'react';
import { Route, withRouter } from 'react-router';
import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withState,
} from 'recompose';
import queryString from 'query-string';
import {
  socialLoginAuthenticateWithAuthCode,
  socialLoginAuthenticateWithAccessToken,
  socialLoginRevoke,
} from '../Common/Providers/SocialLoginApi';
import { HTMLTrans, withTranslationProvider } from '../Common/Translations';
import { hasUserDetailManager } from '../Auth/UserDetailManager';
import { hasTokenManager } from '../Auth/TokenManager';
import { withModal } from '../Common/Modal/Modal';
import Spinner from '../Common/Spinner';
import { showErrorMsg } from '../Common/ErrorMsg';
import { showSuccessMsg } from '../Common/SuccessMsg';

const toDashboard = ({ router, tRoute }) => () => router.push(tRoute('/account/dashboard/personal'));

const verifyRedirectResponse = ({
  location: {
    search,
  },
  routeParams: {
    provider,
  },
  setIsUnlinkAction,
  t,
}) => () => {
  const {
    code = '',
    state = '',
    ...params
  } = camelize(queryString.parse(search));
  const { action = '' } = state
    .split('|')
    .reduce((obj, item) => {
      const data = item.split('__');

      return data.length > 1
        ? {
          ...obj,
          [data[0]]: data[1],
        }
        : obj;
    }, {});

  if (action === 'unlink') {
    setIsUnlinkAction(true);
  }

  return new Promise((resolve, reject) => {
    if (code.length > 0) {
      resolve({
        provider,
        code,
      });
    }

    const {
      errorDescription = t('social_login.callback_authorisation_rejected', 'Permission denied.'),
    } = params;

    reject(errorDescription);
  });
};

const checkHasUnlinkedAccount = ({
  router,
  tRoute,
}) => (provider, code) => new Promise((resolve) => {
  socialLoginAuthenticateWithAuthCode(
    provider,
    code,
    1,
    () => router.push(tRoute('/account/dashboard/personal'))
  )
    .done(({ data }) => resolve(camelize(data)), 'json');
});

const revokeSocialAccount = ({ router, t, tRoute }) => (provider) => {
  const redirectPath = '/account/dashboard/personal';

  return new Promise(
    resolve => socialLoginRevoke(provider, () => router.push(tRoute(redirectPath)))
      .done(() => {
        showSuccessMsg(t('social_login.callback_unlink_successful', 'Successfully unlinked.'));
        resolve(redirectPath);
      }));
};

const authenticate = props => () => {
  const {
    accessToken,
    isUnlinkAction,
    revokeSocialAccount: revoke,
    refreshToken,
    router,
    routeParams: {
      provider,
    },
    setDetail,
    setToken,
    tokenExpiresAt,
    tRoute,
    updateToken,
  } = props;
  const authPromise = new Promise(resolve => socialLoginAuthenticateWithAccessToken(
    provider,
    accessToken,
    tokenExpiresAt,
    refreshToken,
    0,
    () => router.push(tRoute(atob(new URLSearchParams(router.location.search).get("state"))))
  )
    .done(response => resolve(response), 'json'));

  authPromise
    .then(({ data: { token, user } }) => {
      if (isUnlinkAction === true) {
        return revoke(provider);
      }

      setToken(token);
      setDetail(user);

      if (updateToken) {
        updateToken(token);
      }

      return new Promise(resolve => resolve(atob(new URLSearchParams(router.location.search).get("state"))));
    })
    .then((url) => {
      window.location = tRoute(url);
    })
};

const confirmationModal = ({
  authenticate: onClickYes,
  showModal,
  user: {
    email,
  },
}) => (
  <div>
    <div className="icon mb-5">
      <i className="fa fa-exclamation fa-5x fa-round-border text-primary-lighten-1"
         aria-hidden="true"></i>
    </div>
    <div>
      <h2 className="text-primary ProximaNova-Bold">Oops!</h2>
      <span className="text-primary">
        <HTMLTrans id="social_login.callback_confirmation" variables={{
          email,
        }}>An account with this email {email} has already been
        created. Do you want to link to this account?</HTMLTrans>
      </span>
    </div>
    <div className="modal-footer d-flex justify-content-center">
      <button className="btn btn-outline-primary" onClick={() => showModal(false)}>
        <HTMLTrans id="social_login.callback_confirmation_cancel_button">Cancel</HTMLTrans>
      </button>
      <button className="btn btn-primary px-5" onClick={onClickYes}>
        <HTMLTrans id="social_login.callback_confirmation_yes_button">Yes</HTMLTrans>
      </button>
    </div>
  </div>
);

const callbackContainer = ({ open }) => !open && <Spinner/>;

const enhance = compose(
  hasUserDetailManager,
  hasTokenManager,
  withRouter,
  withTranslationProvider,
  withState('accessToken', 'setAccessToken', false),
  withState('refreshToken', 'setRefreshToken', false),
  withState('tokenExpiresAt', 'setTokenExpiresAt', null),
  withState('user', 'setUser', {}),
  withState('open', 'showModal', false),
  withState('isUnlinkAction', 'setIsUnlinkAction', false),
  withProps({
    size: 'sm',
    modalBody: confirmationModal,
  }),
  withHandlers({
    revokeSocialAccount,
  }),
  withHandlers({
    toDashboard,
    verifyRedirectResponse,
    checkHasUnlinkedAccount,
    authenticate,
    closeCallback: toDashboard,
  }),
  lifecycle({
    componentDidMount() {
      this.props.verifyRedirectResponse()
        .then(({ provider, code }) => this.props.checkHasUnlinkedAccount(provider, code))
        .then(({
          accessToken,
          refreshToken,
          tokenExpiresAt = '',
          hasUnlinkedAccount,
          user,
        }) => {
          this.props.setUser(user);
          this.props.setAccessToken(accessToken);
          this.props.setRefreshToken(refreshToken);
          this.props.setTokenExpiresAt(tokenExpiresAt);
          if (hasUnlinkedAccount === true) {
            this.props.showModal(true);
          } else {
            this.props.authenticate();
          }
        })
        .catch((error) => {
          showErrorMsg(error);
          this.props.toDashboard();
        });
    },
  }),
  withModal,
);

const SocialLoginRoutes =
  (
    <Route label='Social Login' path='sociallogin/:provider/callback'
           component={enhance(callbackContainer)}>
    </Route>
  );

export default SocialLoginRoutes;



// WEBPACK FOOTER //
// ./src/Routes/SocialLoginRoutes.js